import * as routerActions from 'connected-react-router'
import { stringify, parse } from 'querystringify'

// Types
import { Dispatch, APIAction, SimpleAction, QueryObject } from 'types'

// Selectors
import { selectPurgedSearchString } from 'modules/browse/selectors'

// Utils
import { trackList, track, searchData } from 'normalize'
import { TRACKS_PER_PAGE } from 'components/browse/tracklist'

export const keyDict = {
  bpm_over: 'BPM__gte',
  bpm_under: 'BPM__lte',
  categories: 'license_category__in',
  q: 'search',
  release_year_after: 'release_year__gte',
  release_year_before: 'release_year__lte',
  regions: 'region__in',
  keys: 'tonal__in',
  owner: 'owner',
  browse: 'browse',
  size: 'limit',
  genres: 'genre__in',
}

export const orderDict = {
  1: '-popularity',
  2: '-publish_date',
  3: 'title',
  4: '-title',
  5: '-release_year',
}

const replaceCommasWithUnderscore = (value: string | number) => (typeof value === 'string' ? value.replace(/,/g, '__') : value)

export const parseQueryForSearch = (
  queryString: string,
  page?: number,
  tracks_per_page?: number,
  defaultToMasterTrackType = true,
): string => {
  const queryObject = parse(selectPurgedSearchString(queryString))
  const haystackTranslatedQueryString: Record<string, unknown> = Object.keys(queryObject).reduce((agg, key) => {
    const translation = keyDict[key as keyof typeof keyDict]
    const value = queryObject[key as keyof typeof queryObject]
    if (!translation) {
      return {
        ...agg,
        [key]: value,
      }
    }
    return {
      ...agg,
      [translation]: replaceCommasWithUnderscore(value),
    }
  }, {})

  const {
    types,
    ordering,
    BPM__gte,
    BPM__lte,
    owner,
    owned,
    browse,
    multitrack,
    song,
    collection,
    ids,
    limit,
    ...haystackTranslatedQueryStringWithoutParams
  } = haystackTranslatedQueryString

  const tracksPerPage = tracks_per_page || TRACKS_PER_PAGE

  let newQuery: Record<string, unknown> = {
    ...haystackTranslatedQueryStringWithoutParams,
    ordering: orderDict[(ordering || '1') as keyof typeof orderDict],
    ...(page ? { page } : {}),
  }
  if (BPM__gte && BPM__gte === BPM__lte) {
    newQuery = { ...newQuery, ...{ BPM: BPM__gte } }
  } else {
    newQuery = { ...newQuery, ...{ ...(BPM__gte ? { BPM__gte } : {}) } }
    newQuery = { ...newQuery, ...(BPM__lte ? { BPM__lte } : {}) }
  }
  if (page) {
    newQuery = { ...newQuery, offset: tracksPerPage * (page - 1), limit: limit || tracksPerPage }
  }
  if (owner) {
    newQuery = { ...newQuery, owner }
  }
  if (browse) {
    newQuery = { ...newQuery, browse }
  }
  if (multitrack) {
    newQuery = { ...newQuery, track_count__gt: 1 }
  }
  if (song) {
    newQuery = { ...newQuery, song }
  }
  if (collection) {
    newQuery = { ...newQuery, collection }
  }
  if (ids) {
    newQuery = { ...newQuery, ids: replaceCommasWithUnderscore(ids as string) }
  }
  if (owned) {
    newQuery = { ...newQuery, owned }
  }
  if (types || song || collection || ids || owned) {
    newQuery = { ...newQuery, ...(types ? { type__in: replaceCommasWithUnderscore(types as string) } : {}) }
  } else if (defaultToMasterTrackType) {
    newQuery = { ...newQuery, type__in: 17 } // default only show master
  }

  // NOTE(Kitti): If search string then use _score sorting
  if (newQuery.search) {
    const { ordering, ...rest } = newQuery // eslint-disable-line
    newQuery = { ...rest, ordering: '-_score' }
  }
  return stringify(newQuery)
}

export const actions = {
  updateSearchWithQuery: (newQuery: QueryObject) => (dispatch: Dispatch) => {
    // (kitti): Sort query params for consistent caching
    const newSearch = `?${stringify(newQuery).split('&').sort().join('&')}`
    return dispatch(routerActions.push(newSearch))
  },

  fetchSearchConfig: (queryString = '', page = 1, tracksPerPage = 1): APIAction => {
    const parsedQueryString = parseQueryForSearch(queryString, page, tracksPerPage, false)

    return {
      CALL_API: {
        type: 'FETCH_SEARCH_CONFIG',
        method: 'GET',
        endpoint: `/api/search/config/?${parsedQueryString}`,
        requestKey: queryString,
        actionParams: { queryString },
      },
    }
  },

  fetchSearchData: (): APIAction => {
    return {
      CALL_API: {
        type: 'FETCH_SEARCH_DATA',
        method: 'GET',
        endpoint: '/api/tracks/searchdata',
        schema: searchData,
      },
    }
  },

  resetLastPageLoaded: (queryString: string): SimpleAction => ({
    type: 'RESET_LAST_PAGE_LOADED',
    data: { queryString },
  }),

  fetchTrack: (trackId: number) => ({
    CALL_API: {
      type: 'FETCH_TRACK',
      method: 'GET',
      endpoint: `/api/search/track/${trackId}/`,
      schema: track,
    },
  }),

  fetchTracksBySearchQuery: (queryString: string, page = 1): APIAction => {
    const parsedQueryString = parseQueryForSearch(queryString, page)

    return {
      CALL_API: {
        type: 'FETCH_TRACKS_BY_SEARCH_QUERY',
        method: 'GET',
        endpoint: `/api/search/track/?${parsedQueryString}`,
        schema: trackList,
        requestKey: queryString,
        actionParams: { page, queryString },
        merge: true,
      },
    }
  },

  filterToggle: (): SimpleAction => ({
    type: 'FILTER_TOGGLE',
  }),

  setDisplayMode: (value: string) => (dispatch: Dispatch) => {
    dispatch(actions._setDisplayMode('')) // reset before setting again, prevents out of sync localstorage/state
    dispatch(actions._setDisplayMode(value))
  },

  _setDisplayMode: (value: string): SimpleAction => ({
    type: 'SET_DISPLAY_MODE',
    data: value,
  }),

  showAdminMode: (): SimpleAction => ({
    type: 'ADMIN_MODE_SHOW',
  }),
}
